import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import App from './App';
import { UsersProvider } from './contexts/UsersContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { TenantProvider } from './contexts/TenantContext';
import reportWebVitals from './reportWebVitals';
import { logErrorToFirestore } from './components/ErrorBoundary';
import { isChunkError, attemptChunkRecovery } from './lib/chunkRecovery';
import { isClientSignPage } from './sign/clientSignPath';

// RW-3278: the client signing page (/sign/<slug>) renders on its own, with no
// providers, login or error/analytics reporting — its URL is a signing link.
const CLIENT_SIGN = isClientSignPage();
const ClientSignPage = React.lazy(() => import('./lab/sign/ClientSignPage'));

// Global error + unhandled promise rejection handlers.
// Stale-chunk / version-skew detection + auto-reload recovery lives in
// ./lib/chunkRecovery so the React error boundary can reuse the same logic (RW-859).
if (typeof window !== 'undefined' && !CLIENT_SIGN) {
  window.addEventListener('error', (ev) => {
    const msg = ev.message || String(ev.error);
    const stack = ev.error?.stack || '';
    if (isChunkError(msg, stack)) {
      // Don't log transient stale-chunk / version-skew errors — the reload
      // recovers them and a ticket would be pure noise (Rule 17).
      // attemptChunkRecovery logs only if recovery fails after repeated reloads.
      attemptChunkRecovery({ message: msg, stack, log: logErrorToFirestore });
      return;
    }
    // RW-1535: no userId here — logErrorToFirestore resolves the signed-in uid.
    // This used to hardcode 'anonymous', which is why every unhandled_error in
    // prod was unattributed even when a known user hit it.
    logErrorToFirestore({
      type: 'unhandled_error',
      message: msg,
      stack,
    });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason;
    const msg = reason?.message || String(reason);
    const stack = reason?.stack || '';
    if (isChunkError(msg, stack)) {
      attemptChunkRecovery({ message: msg, stack, log: logErrorToFirestore });
      return;
    }
    // RW-1535: see above — uid resolved centrally, not hardcoded.
    logErrorToFirestore({
      type: 'unhandled_rejection',
      message: msg,
      stack,
    });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,   // 5 minutes
      gcTime: 1000 * 60 * 30,     // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
if (CLIENT_SIGN) {
  root.render(
    <React.StrictMode>
      <React.Suspense fallback={null}>
        <ClientSignPage />
      </React.Suspense>
    </React.StrictMode>
  );
} else root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ViewModeProvider>
        <FeatureFlagsProvider>
          <ImpersonationProvider>
            <TenantProvider>
              <UsersProvider>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </UsersProvider>
            </TenantProvider>
          </ImpersonationProvider>
        </FeatureFlagsProvider>
      </ViewModeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

// Register service workers:
//   - /firebase-messaging-sw.js — background push notifications (registered here)
//   - /sw.js — app-shell caching AND the "new version available" prompt — is
//     owned entirely by PWAShell.js. PWAShell surfaces the banner only on a
//     SUBSEQUENT visit and persists per-version dismissal (RW-167), and it owns
//     the controllerchange→reload. A second, eager, non-persistent banner used
//     to live here too (rRyCFjsX: "banner shows when there's no new version and
//     won't stay dismissed"), so index.js no longer registers /sw.js or renders
//     any update UX — that would just duplicate PWAShell and re-break dismissal.
if ('serviceWorker' in navigator && !CLIENT_SIGN) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/firebase-cloud-messaging-push-scope' }).catch((err) => {
      console.warn('[firebase-messaging-sw.js] registration failed', err?.message);
    });
  });
}

reportWebVitals();
